import { useState, useEffect, useRef } from 'react';
import './CountdownBar.css';

// GLOBAL "time remaining" countdown bar. A thin horizontal bar that drains from full to empty
// over `durationMs`, left-anchored (the fill shrinks right-to-left, like the per-seat time-bank
// bar). It is deliberately generic so any timed game moment can reuse it:
//   - the Start/End phase beats (GameTable renders one while game.currentPhase is "start"/"end")
//   - future Pup-card interaction windows (a timed window to play/respond to a card)
//
// Anchoring: pass `startTime` (epoch ms) to anchor the countdown to a server/shared clock so it
// stays accurate across re-renders and reflects elapsed time if mounted late; omit it to anchor
// to first mount (fine for a beat the client observes at its start, like the phase bar). To
// restart the bar for a brand-new window, either pass a fresh `startTime` or remount it via a
// changing React `key`.
//
// Styling: the fill is blue by default (see CountdownBar.css). Pass `color` (any CSS color) to
// override, and `className` to position/size the wrapper for a given placement.
const CountdownBar = ({ durationMs = 2000, startTime, color, className = '' }) => {
  // Anchor timestamp: the shared startTime when provided, else the moment we first mounted.
  const anchorRef = useRef(startTime != null ? startTime : Date.now());
  // A new startTime (bar reused for a fresh window without a remount) re-anchors the countdown.
  if (startTime != null && startTime !== anchorRef.current) anchorRef.current = startTime;
  const anchor = anchorRef.current;

  const [fill, setFill] = useState(1); // 0..1 fraction of time remaining

  useEffect(() => {
    const tick = () => {
      const remaining = durationMs - (Date.now() - anchor);
      setFill(durationMs > 0 ? Math.max(0, remaining / durationMs) : 0);
      return remaining;
    };
    tick();
    const id = setInterval(() => {
      if (tick() <= 0) clearInterval(id); // stop ticking once drained
    }, 60);
    return () => clearInterval(id);
  }, [anchor, durationMs]);

  return (
    <div
      className={`countdown-bar ${className}`.trim()}
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(fill * 100)}
    >
      <div
        className="countdown-fill"
        style={{ width: `${fill * 100}%`, ...(color ? { background: color } : {}) }}
      />
    </div>
  );
};

export default CountdownBar;
